import { Pause, Play } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib/utils'

type VideoProps = {
  src: string
  poster?: string
  controls?: boolean
  autoPlay?: boolean
  muted?: boolean
  className?: string
  /** Qué muestra el video, para lectores de pantalla. */
  label?: string
}

/**
 * ¿Arranca solo? Sí, salvo que lo apague la entrada (`autoPlay={false}`) o quien pidió menos
 * movimiento en su sistema (`prefers-reduced-motion`). Vale igual en la página «Novedades» y en el
 * modal «¡Hay novedades!».
 */
export function resolveVideoAutoPlay(options: { autoPlay: boolean; reducedMotion: boolean }) {
  return options.autoPlay && !options.reducedMotion
}

/**
 * Arranca un video que tiene que correr solo. El atributo `autoplay` no alcanza cuando el `<video>`
 * se inserta tarde —el modal se abre en un portal un par de segundos después de cargar la página— y
 * hay navegadores que lo ignoran: por eso se le pide `play()` explícito al montarlo.
 *
 * Si el navegador lo bloquea (política de autoplay, ahorro de datos), avisa con `onBlocked` para que
 * el botón ofrezca reproducirlo en vez de mostrar una pausa que no pausa nada.
 */
export function startVideoAutoPlay(
  video: Pick<HTMLVideoElement, 'paused' | 'play'>,
  onBlocked: () => void
) {
  if (!video.paused) return
  video.play().catch(onBlocked)
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * El video de una entrada del changelog: en loop y mudo, con póster y un botón para pausarlo.
 *
 * El botón no es decorativo: un loop de más de 5 s que arranca solo TIENE que poder pausarse
 * (WCAG 2.2.2). Con `controls` se usan los nativos y el botón propio sobra.
 */
export function Video({
  src,
  poster,
  controls = false,
  autoPlay = true,
  muted = true,
  className = 'w-full rounded-lg',
  label
}: VideoProps) {
  const [reducedMotion] = useState(prefersReducedMotion)
  const shouldAutoPlay = resolveVideoAutoPlay({ autoPlay, reducedMotion })
  const [playing, setPlaying] = useState(shouldAutoPlay)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!shouldAutoPlay || !video) return
    startVideoAutoPlay(video, () => setPlaying(false))
  }, [shouldAutoPlay])

  const toggle = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play().catch(() => setPlaying(false))
    } else {
      video.pause()
    }
  }

  return (
    <div className="relative my-6 w-full min-w-0">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls={controls}
        autoPlay={shouldAutoPlay}
        loop={true}
        muted={muted}
        playsInline={true}
        className={className}
        preload="metadata"
        aria-label={label}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {!controls && (
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? 'Pausar video' : 'Reproducir video'}
          className={cn(
            'absolute right-3 bottom-3 flex size-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm',
            'transition-transform duration-150 ease-out active:scale-95',
            'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 focus-visible:outline-none'
          )}
        >
          {playing ? (
            <Pause className="size-4" aria-hidden="true" />
          ) : (
            <Play className="size-4 translate-x-px" aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  )
}
